import React, { useEffect, useState } from "react";
import { ChevronRight, Pencil } from "lucide-react";
import { fmtCurrency } from "../lib/formatters";
import { REFERENCE_ANCHORS, useDevMode } from "../lib/anchors";
import AnchorBadge from "./AnchorBadge";

// "Results at a Glance" — 5 headline output metrics with a Scenarios pill
// (click opens the History modal). Boxed navy border to match Dave's design.
export default function ResultsAtAGlance({
  calc,
  association,
  variables,
  scenarioCount,
  activeScenarioName,
  activeScenarioTitle,
  onOpenHistory,
  onPlanYearChanged,
  onNameActive,
}) {
  const [devMode] = useDevMode();
  const [pmpmOpen, setPmpmOpen] = useState(false);
  const [pmpmHintOff, setPmpmHintOff] = useState(false);
  useEffect(() => {
    const h = (e) => setPmpmOpen(!!e.detail);
    window.addEventListener("ureserve:pmpm-reveal-state", h);
    return () => window.removeEventListener("ureserve:pmpm-reveal-state", h);
  }, []);
  if (!calc || !variables || !association) return null;

  const anchor = REFERENCE_ANCHORS[association.aid] || null;

  const y1Adj = (calc.annual_contribution || 0) - (calc.year1_contribution || 0);
  const y1Budget = calc.year1_contribution; // Y1 payment schedule for members

  const rows = [
    {
      label: "Year 1 Contribution",
      value: fmtCurrency(calc.annual_contribution),
      testid: "raag-y1-contribution",
      badge: devMode && anchor ? { actual: calc.annual_contribution, expected: anchor.annual_contribution, testid: "anchor-annual-contribution" } : null,
    },
    {
      label: "Year 1 Adjustment",
      // 2026-02-27 (evening, Dave) — Parens = decrease only. See
      // detailed note in AssociationScreen.jsx. `y1Adj = annual −
      // year1`, so an UPLIFT (adjustment_pct > 0) makes y1Adj
      // NEGATIVE — render plain. A DISCOUNT flips y1Adj positive —
      // wrap in parens.
      value:
        y1Adj > 0
          ? `-${fmtCurrency(y1Adj)}`
          : y1Adj < 0
            ? fmtCurrency(Math.abs(y1Adj))
            : "$0",
      accent: y1Adj > 0,
      testid: "raag-y1-adjustment",
    },
    {
      label: "Year 1 Budget Amount",
      value: fmtCurrency(y1Budget),
      testid: "raag-y1-budget",
      badge: devMode && anchor ? { actual: y1Budget, expected: anchor.year1_contribution, testid: "anchor-year1-contribution" } : null,
    },
    {
      label: `Per Member (${association.unit_count}) per month`,
      value: fmtCurrency(calc.per_member_per_month, 2),
      testid: "raag-pmpm",
      onClick: () => window.dispatchEvent(new Event("ureserve:toggle-per-member-reveal")),
      badge: devMode && anchor ? { actual: calc.per_member_per_month, expected: anchor.per_member_per_month, digits: 2, testid: "anchor-pmpm" } : null,
    },
    {
      label: "Special Assessments",
      value:
        (calc.special_assessments_total || 0) > 0.5
          ? `-${fmtCurrency(calc.special_assessments_total)}`
          : "$0",
      accent: true,
      testid: "raag-specials",
    },
  ];

  return (
    <div
      /* 2026-02-16 (Dave) — swap the hand-rolled wrapper for the shared
         `.panel-compact` utility so this panel uses the same padding
         tokens (clamp(6px, 0.5vw, 10px) vertical) as the sibling
         Funding Mix and Reserve Summary panels. Without this, at
         viewports ≤ 1440 the hardcoded 10 px padding here stayed
         fixed while the siblings shrank to ~7 px, pushing THIS
         panel's coral rule 3-4 px lower and breaking horizontal
         alignment across the three top-row panels. */
      className="panel-compact attention-border results-border flex flex-col"
      data-testid="results-at-a-glance"
      style={{ minHeight: 0 }}
    >
      {/* 2026-02-23 (Dave) — Baseline restructure. Override the shared
          `.dash-panel-header` `align-items: center` with baseline for
          THIS panel only so the 14 px Plan Year label lines up on its
          text baseline (not descender bottom) with the smaller title
          and Scenarios-pill text. Plan Year is now a real flex child
          with `flex: 1 + text-align: center` — centered in the
          remaining space between title (left) and pill (right). */}
      <div
        className="dash-panel-header"
        style={{ alignItems: "center" }}
      >
        <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy shrink-0">
          Results
        </div>
        <div
          className="flex-1 min-w-0 text-right truncate font-semibold pl-2"
          style={{ color: "#4A5560" }}
          title={activeScenarioTitle || ""}
          data-testid="raag-active-scenario-name"
        >
          {activeScenarioTitle === undefined ? "" : <ScenarioName name={activeScenarioTitle} />}
        </div>
      </div>
      <dl
        className="grid grid-cols-[1fr_auto] gap-x-3 text-[10.5px] flex-1"
        style={{ alignContent: "space-between" }}
      >
        {rows.map((r) => {
          // 2026-09-14 (Dave) — Special Assessments is the last row
          // in the panel; suppress its bottom rule so the panel
          // doesn't close with a stray light-gray underline.
          const isLast = r.label === "Special Assessments";
          const rule = isLast ? "none" : "1px solid #C9D6E2";
          return (
          <React.Fragment key={r.label}>
            <dt
              className="text-[#3F4B57] text-[10.5px] tracking-[0.08em] uppercase font-semibold self-center py-1"
              style={{ borderBottom: rule }}
            >
              {r.label}
            </dt>
            <dd
              data-testid={r.testid}
              className="font-mono tabular-nums text-right font-semibold self-center py-1"
              style={{
                color: r.accent ? "#EA6A47" : "var(--u-ink)",
                borderBottom: rule,
              }}
            >
              <div className="flex flex-col items-end gap-0.5">
                {/* 2026-02-17 (Dave) — Accounting-style paren overhang.
                   For paren-wrapped negatives like "(1,234)" we shift the
                   whole value 1ch to the right so the last digit aligns
                   with the last digit of positive rows in the same
                   column, and the trailing ")" overhangs by one
                   monospace character. `font-mono tabular-nums` on the
                   parent guarantees the 1ch offset matches ")". */}
                {r.onClick ? (
                  <button
                    type="button"
                    onClick={(e) => { setPmpmHintOff(true); r.onClick(e); }}
                    onMouseLeave={() => setPmpmHintOff(false)}
                    aria-label="Show Per Member Per Month on the chart"
                    className="group relative inline-flex items-center gap-0.5 -mr-2"
                    data-testid={`${r.testid}-reveal-btn`}
                  >
                    <span
                      className={"pointer-events-none absolute right-0 bottom-full mb-1 whitespace-nowrap rounded-[3px] border border-[#EA6A47] bg-[#1C4E80] text-white text-[11px] font-semibold px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10" + (pmpmHintOff ? " !opacity-0" : "")}
                      data-testid="raag-pmpm-tooltip"
                    >
                      Per Member Per Month
                    </span>
                    <ChevronRight size={14} className={"text-slate group-hover:text-navy transition-transform duration-200 " + (pmpmOpen ? "rotate-90" : "")} aria-hidden="true" />
                    <span className="rounded-[3px] bg-[#EAEFF5] group-hover:bg-[#D3DCE5] transition-colors px-2 py-0.5 -my-0.5">
                      {r.value}
                    </span>
                  </button>
                ) : (
                <span
                  style={
                    typeof r.value === "string" && r.value.startsWith("(") && r.value.endsWith(")")
                      ? { marginRight: "-1ch" }
                      : undefined
                  }
                >
                  {r.value}
                </span>
                )}
                {r.badge && (
                  <AnchorBadge
                    actual={r.badge.actual}
                    expected={r.badge.expected}
                    digits={r.badge.digits}
                    testid={r.badge.testid}
                  />
                )}
              </div>
            </dd>
          </React.Fragment>
          );
        })}
      </dl>
    </div>
  );
}

// Scenario name in a header. Unnamed: single-click opens the name box; named: double-click renames.
export function ScenarioName({ name, onName }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const label = name ? name : <span className="text-[#A3ADB8] font-normal">[Unnamed Scenario]</span>;
  if (!onName) return label;
  const open = () => { setDraft(name || ""); setEditing(true); };
  if (!editing) {
    return name ? (
      <span onDoubleClick={open} title="Double-click to rename" className="group cursor-text inline-flex items-center gap-1 max-w-full" data-testid="scenario-name-dblclick">
        <button
          type="button"
          onClick={open}
          aria-label="Rename scenario"
          className="shrink-0 text-slate opacity-0 group-hover:opacity-100 hover:text-navy transition-opacity"
          data-testid="scenario-name-rename-hint"
        >
          <Pencil size={10} />
        </button>
        <span className="truncate">{name}</span>
      </span>
    ) : (
      <button type="button" onClick={open} title="Click to name this scenario" className="hover:underline" data-testid="raag-name-scenario-btn">
        {label}
      </button>
    );
  }
  const cancel = () => { setEditing(false); setDraft(""); setErr(""); };
  const save = async () => {
    const next = draft.trim();
    if (!next || next === name) { cancel(); return; }
    setBusy(true); setErr("");
    try { await onName(next); cancel(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1 justify-end">
      {err && <span className="text-coral text-[10px] font-normal truncate" title={err} data-testid="raag-name-scenario-error">{err}</span>}
      <input
        autoFocus
        value={draft}
        maxLength={56}
        disabled={busy}
        placeholder="Scenario name"
        onFocus={(e) => e.target.select()}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } else if (e.key === "Escape") { e.preventDefault(); cancel(); } }}
        onBlur={() => { if (!busy) save(); }}
        className="w-44 border border-navy rounded-[3px] px-1 py-0 text-[11.5px] font-normal text-ink bg-white outline-none"
        data-testid="raag-name-scenario-input"
      />
    </span>
  );
}
